import React from 'react';
import {
  UserGroupIcon,
  UserIcon,
  BuildingOfficeIcon,
  CheckIcon,
  SparklesIcon,
  CheckCircleIcon,
  ComputerDesktopIcon,
  DevicePhoneMobileIcon,
  EyeIcon,
  CursorArrowRaysIcon,
  MagnifyingGlassPlusIcon,
  MagnifyingGlassMinusIcon,
  ArrowRightIcon,
  LightBulbIcon,
  MinusCircleIcon
} from '@heroicons/react/24/outline';

export default function MembersHelp() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-green-50">
      <div className="max-w-6xl mx-auto px-4 py-12">
        {/* Header Section */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-emerald-500 to-green-600 rounded-full mb-6">
            <UserGroupIcon className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Team Members Management</h1>
          <p className="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto">
            Visually organize and assign your staff to the right teams with intuitive drag-and-drop functionality
          </p>
        </div>

        {/* Overview Card */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-8 border border-gray-100">
          <div className="flex items-start space-x-2 md:space-x-4">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center">
                <LightBulbIcon className="w-6 h-6 text-emerald-600" />
              </div>
            </div>
            <div>
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-3">What is Team Members Management?</h2>
              <p className="text-gray-600 text-base md:text-lg leading-relaxed">
                This is your visual command center for workforce organization. See all your staff at a glance, assign them to departments with simple selections, and manage team compositions effortlessly. It's like having a digital org chart that you can update in real-time.
              </p>
            </div>
          </div>
        </div>

        {/* System Overview Diagram */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-8 border border-gray-100">
          <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-6 text-center">How Team Assignment Works</h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Step 1 */}
            <div className="text-center">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-emerald-600">1</span>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Review Unassigned Staff</h3>
              <p className="text-gray-600 text-sm">Check the unassigned staff pool to see who needs department placement</p>
            </div>

            {/* Arrow */}
            <div className="hidden md:flex items-center justify-center">
              <ArrowRightIcon className="w-8 h-8 text-emerald-400" />
            </div>

            {/* Step 2 */}
            <div className="text-center">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-emerald-600">2</span>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Select & Assign</h3>
              <p className="text-gray-600 text-sm">Choose staff members and assign them to appropriate departments</p>
            </div>
          </div>

          {/* Mobile flow indicator */}
          <div className="md:hidden flex justify-center items-center space-x-4 mt-6">
            <span className="text-emerald-600 font-medium">1 → 2</span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
            {/* Step 3 */}
            <div className="text-center">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-emerald-600">3</span>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Move Between Teams</h3>
              <p className="text-gray-600 text-sm">Reassign staff between departments as roles and responsibilities change</p>
            </div>

            {/* Arrow */}
            <div className="hidden md:flex items-center justify-center">
              <ArrowRightIcon className="w-8 h-8 text-emerald-400" />
            </div>

            {/* Step 4 */}
            <div className="text-center">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-emerald-600">4</span>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Monitor & Adjust</h3>
              <p className="text-gray-600 text-sm">Keep team compositions balanced and update assignments as needed</p>
            </div>
          </div>

          {/* Mobile flow indicator */}
          <div className="md:hidden flex justify-center items-center space-x-4 mt-6">
            <span className="text-emerald-600 font-medium">3 → 4</span>
          </div>
        </div>

        {/* Key Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {/* Feature 1 */}
          <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100 hover:shadow-lg transition-shadow">
            <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center mb-4">
              <UserIcon className="w-6 h-6 text-emerald-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Unassigned Staff Pool</h3>
            <p className="text-gray-600 text-sm">
              Dedicated sidebar showing all staff members not yet assigned to any department, with role and management level indicators
            </p>
          </div>

          {/* Feature 2 */}
          <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100 hover:shadow-lg transition-shadow">
            <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center mb-4">
              <CheckIcon className="w-6 h-6 text-emerald-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Individual Assignment</h3>
            <p className="text-gray-600 text-sm">
              Select individual staff members and assign them to specific departments using dropdown menus and confirmation buttons
            </p>
          </div>

          {/* Feature 3 */}
          <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100 hover:shadow-lg transition-shadow">
            <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center mb-4">
              <UserGroupIcon className="w-6 h-6 text-emerald-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Bulk Assignment</h3>
            <p className="text-gray-600 text-sm">
              Select multiple staff members at once and assign them all to the same department with a single action
            </p>
          </div>

          {/* Feature 4 */}
          <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100 hover:shadow-lg transition-shadow">
            <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center mb-4">
              <BuildingOfficeIcon className="w-6 h-6 text-emerald-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Department Columns</h3>
            <p className="text-gray-600 text-sm">
              Visual department cards showing all assigned staff with management level badges and easy removal options
            </p>
          </div>

          {/* Feature 5 */}
          <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100 hover:shadow-lg transition-shadow">
            <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center mb-4">
              <MinusCircleIcon className="w-6 h-6 text-emerald-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Remove from Teams</h3>
            <p className="text-gray-600 text-sm">
              Move staff back to the unassigned pool when they change roles or leave departments
            </p>
          </div>

          {/* Feature 6 */}
          <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100 hover:shadow-lg transition-shadow">
            <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center mb-4">
              <MagnifyingGlassPlusIcon className="w-6 h-6 text-emerald-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Zoom Controls</h3>
            <p className="text-gray-600 text-sm">
              Adjust the view scale to see more or fewer departments at once, optimizing for your screen size
            </p>
          </div>
        </div>

        {/* Management Levels Guide */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-8 border border-gray-100">
          <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-6 text-center">Understanding Management Levels</h2>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <div className="w-8 h-8 bg-gray-500 rounded-full flex items-center justify-center mx-auto mb-2">
                <span className="text-white font-bold text-sm">E</span>
              </div>
              <h3 className="font-semibold text-gray-900 mb-1">Employee</h3>
              <p className="text-sm text-gray-600">Level 1 - Individual contributor</p>
            </div>

            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-2">
                <span className="text-white font-bold text-sm">TM</span>
              </div>
              <h3 className="font-semibold text-gray-900 mb-1">Team Manager</h3>
              <p className="text-sm text-gray-600">Level 2 - Leads small teams</p>
            </div>

            <div className="text-center p-4 bg-green-50 rounded-lg">
              <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-2">
                <span className="text-white font-bold text-sm">SM</span>
              </div>
              <h3 className="font-semibold text-gray-900 mb-1">Senior Management</h3>
              <p className="text-sm text-gray-600">Level 3 - Manages multiple teams</p>
            </div>

            <div className="text-center p-4 bg-purple-50 rounded-lg">
              <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center mx-auto mb-2">
                <span className="text-white font-bold text-sm">D</span>
              </div>
              <h3 className="font-semibold text-gray-900 mb-1">Director</h3>
              <p className="text-sm text-gray-600">Level 4 - Executive leadership</p>
            </div>
          </div>
        </div>

        {/* Tips Section */}
        <div className="bg-gradient-to-r from-emerald-50 to-green-50 rounded-2xl p-6 md:p-8 border border-emerald-100">
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center">
                <SparklesIcon className="w-6 h-6 text-emerald-600" />
              </div>
            </div>
            <div>
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4">Best Practices for Team Assignments</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-start space-x-3">
                  <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <p className="text-gray-700 text-sm">Assign staff to departments based on their primary responsibilities</p>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <p className="text-gray-700 text-sm">Use bulk assignment for new hires joining the same team</p>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <p className="text-gray-700 text-sm">Regularly review team sizes to ensure balanced workloads</p>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <p className="text-gray-700 text-sm">Keep the unassigned pool small by promptly placing new staff</p>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <p className="text-gray-700 text-sm">Consider management levels when assigning team leads</p>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <p className="text-gray-700 text-sm">Use zoom controls to optimize your view for efficient management</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}